// components/ChatBody.js

import React, { useRef, useCallback, useEffect, useState } from "react";
import ChatInput from "./ChatInput";
import TypingIndicator from "./TypingIndicator";
import ResetButton from "./ResetButton";
import AudioPlayer from "./AudioPlayer";
import Typewriter from "./Typewriter"; 
import ReactMarkdown from "react-markdown";
import FeedbackActions from "./FeedbackActions";
import FeedbackModal from "./FeedbackModal";
import { sendMessageToFlowise, sendAudioToFlowise, sendFeedbackToFlowise } from "../utils/flowiseApi";
import { splitParagraphs } from "../utils/helpers";
import { FiX, FiMessageCircle } from "react-icons/fi";
import styles from './ChatBody.module.css'; // Importando o CSS Module

// ... (O resto das suas funções 'calculateTypingDuration' e 'blobToBase64' permanece o mesmo) ...
const MIN_TYPING_DURATION = 700;
const MAX_TYPING_DURATION = 4500;
const DURATION_PER_CHAR = 60;

const calculateTypingDuration = (text) => {
  const duration = MIN_TYPING_DURATION + text.length * DURATION_PER_CHAR;
  return Math.min(duration, MAX_TYPING_DURATION);
};

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};


export default function ChatBody({ chatflowid, apiHost, enableAudio, enableFeedback, contextVars, theme, onClose, headerTitle = "Atendente Virtual", inputValue, onInputChange, messages, setMessages, audioData, setAudioData, onReset }) {
  const messagesContainerRef = useRef(null);
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, messageId: null, rating: null });
  const [detailedFeedbackText, setDetailedFeedbackText] = useState("");
  const lastMessage = messages[messages.length - 1];
  const isBotReplying = lastMessage && lastMessage.from === 'bot' && !lastMessage.isComplete;

  // ... (Todas as suas funções handle (handleScroll, handleSend, etc) permanecem as mesmas) ...
  const handleScroll = useCallback(() => {
    const timer = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  const markLastMessageAsComplete = () => {
    setMessages(currentMessages => {
      const lastIndex = currentMessages.length - 1;
      return currentMessages.map((msg, index) => {
        if (index === lastIndex) {
          return { ...msg, isComplete: true, onComplete: undefined };
        }
        return msg;
      });
    });
  };

  const waitForTyping = (para) => {
    return new Promise(resolve => {
      const onComplete = () => {
        markLastMessageAsComplete();
        resolve(); 
      };
      setMessages(currentMessages => {
        const lastIndex = currentMessages.length - 1;
        return currentMessages.map((msg, index) => 
          index === lastIndex 
            ? { ...msg, type: 'text', text: para, onComplete: onComplete }
            : msg
        );
      });
    });
  };

  const handleSend = async (userMsg) => {
    setMessages(msgs => [...msgs, { from: "user", text: userMsg, isComplete: true }]);
    handleScroll();
    
    setMessages(msgs => [...msgs, { from: "bot", type: "typing" }]);
    handleScroll();
    
    try {
      const sessionId = localStorage.getItem('chat_sessionId');
      const res = await sendMessageToFlowise({ userMsg, chatflowid, apiHost, sessionId, contextVars });
      
      setMessages(currentMessages =>
        currentMessages.map(msg => 
          (msg.type === 'typing' && !msg.id) ? { ...msg, id: res.chatMessageId } : msg
        )
      );
      
      const paragraphs = splitParagraphs(res.text || '');
      
      for (let i = 0; i < paragraphs.length; i++) {
        const para = paragraphs[i];
        if (i === 0) {
          await waitForTyping(para);
        } else {
          const typingDuration = calculateTypingDuration(para);
          setMessages(msgs => [...msgs, { from: "bot", type: "typing", id: res.chatMessageId }]);
          handleScroll();
          await new Promise(r => setTimeout(r, typingDuration));
          await waitForTyping(para);
        }
      }
    } catch (error) {
      console.error("Erro no envio da mensagem:", error);
      setMessages(msgs => msgs.filter(msg => msg.type !== 'typing'));
    }
  };
  
  const handleSendAudio = async (audioBlob) => {
    const tempId = `audio-${Date.now()}`;
    try {
      const base64Audio = await blobToBase64(audioBlob);
      setMessages(msgs => [...msgs, { from: "user", type: "audio", src: base64Audio, id: tempId, isComplete: true }]);
      handleScroll();
      
      setMessages(msgs => [...msgs, { from: "bot", type: "typing" }]);
      handleScroll();

      const sessionId = localStorage.getItem('chat_sessionId');
      
      // --- CORREÇÃO APLICADA AQUI ---
      // O payload agora é montado com a estrutura correta, incluindo 'vars'
      const payload = {
        question: "", // A pergunta vem do áudio, então o campo de texto pode ser vazio
        chatId: sessionId,
        uploads: [{
          data: base64Audio,
          type: "audio",
          name: `audio_${Date.now()}.webm`,
          mime: "audio/webm"
        }],
        overrideConfig: {
          vars: contextVars // Passando o objeto de variáveis corretamente
        }
      };
      
      const res = await sendAudioToFlowise({ payload, chatflowid, apiHost });
      const botResponse = res.text || "";
      const transcription = res.question || "Transcrição não disponível";

      setMessages(currentMessages =>
        currentMessages.map(msg =>
          msg.id === tempId ? { ...msg, transcription: transcription } : msg
        )
      );

      setMessages(currentMessages =>
        currentMessages.map(msg =>
          (msg.type === 'typing' && !msg.id) ? { ...msg, id: res.chatMessageId } : msg
        )
      );

      const paragraphs = splitParagraphs(botResponse);
      for (let i = 0; i < paragraphs.length; i++) {
        const para = paragraphs[i];
        if (i === 0) {
          await waitForTyping(para);
        } else {
          const typingDuration = calculateTypingDuration(para);
          setMessages(msgs => [...msgs, { from: "bot", type: "typing", id: res.chatMessageId }]);
          handleScroll();
          await new Promise(r => setTimeout(r, typingDuration));
          await waitForTyping(para);
        }
      }
    } catch (error) {
      console.error("Erro no envio do áudio:", error);
      setMessages(msgs => msgs.filter(msg => msg.id !== tempId && msg.type !== 'typing'));
    }
  };

  const handleCopy = (messageIdToCopy) => {
    const paragraphsToCopy = messages
      .filter(msg => msg.id === messageIdToCopy && msg.type === 'text')
      .map(msg => msg.text);

    const fullText = paragraphsToCopy.join('\n\n');

    navigator.clipboard.writeText(fullText);
  };

  const handleOpenFeedbackModal = (rating, messageId) => {
    setDetailedFeedbackText("");
    setFeedbackModal({ isOpen: true, messageId, rating });
  };
  
  const handleCloseFeedbackModal = () => {
    setFeedbackModal({ isOpen: false, messageId: null, rating: null });
    setDetailedFeedbackText("");
  };

  const handleSubmitFeedback = async (content) => {
    const { messageId, rating } = feedbackModal;
    if (!messageId || !rating) return;
    
    try {
      const sessionId = localStorage.getItem('chat_sessionId');
      await sendFeedbackToFlowise({
        apiHost,
        chatflowid,
        messageId,
        rating,
        content: content || "",
        chatId: sessionId
      });

      setMessages(currentMessages =>
        currentMessages.map(msg => {
          if (msg.id === messageId) {
            return { ...msg, feedback: rating };
          }
          return msg;
        })
      );
    } catch (error) {
      console.error("Erro ao enviar feedback:", error);
    } finally {
      handleCloseFeedbackModal();
    }
  };

  const handleModalSubmit = () => {
    handleSubmitFeedback(detailedFeedbackText);
  };
  
  const handleModalClose = () => {
    handleSubmitFeedback("");
  };

  return (
    <div className={styles.chatBody}>
      <div className={styles.chatHeader} style={{ background: theme.primary }}>
        {headerTitle && (
          <span className={styles.headerTitle}>
            <FiMessageCircle />
            {headerTitle}
          </span>
        )}
        <div className={styles.headerActions}>
          <ResetButton onReset={onReset} theme={theme} />
          <button className={styles.closeBtn} onClick={onClose} title="Fechar chat">
            <FiX />
          </button>
        </div>
      </div>

      <div className={styles.messages} ref={messagesContainerRef}>
        {messages.map((msg, i) => {
          const nextMsg = messages[i + 1];
          const isLastInGroup = !nextMsg || nextMsg.from === 'user' || nextMsg.id !== msg.id;

          return (
            <div key={i} className={`${styles.msgContainer} ${styles[msg.from]}`}>
              <div className={`${styles.msg} ${styles[msg.from]}`}>
                {msg.type === 'typing' ? ( <TypingIndicator /> ) : 
                 msg.type === 'audio' ? ( <AudioPlayer src={msg.src} transcription={msg.transcription} /> ) : 
                 msg.from === 'bot' ? (
                  <Typewriter 
                    text={msg.text} 
                    onCharacterTyped={handleScroll} 
                    onTypingComplete={msg.onComplete}
                    isComplete={msg.isComplete}
                  />
                ) : (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                )}
              </div>
              
              {enableFeedback && msg.from === 'bot' && msg.isComplete && i > 0 && isLastInGroup && (
                <FeedbackActions 
                  message={msg}
                  onCopy={() => handleCopy(msg.id)}
                  onFeedback={handleOpenFeedbackModal}
                />
              )}
            </div>
          );
        })}
      </div>
      
      <ChatInput 
        onSend={handleSend} 
        onSendAudio={handleSendAudio} 
        theme={theme}
        inputValue={inputValue}
        onInputChange={onInputChange}
        enableAudio={enableAudio}
        audioData={audioData}
        setAudioData={setAudioData}
        isBotReplying={isBotReplying}
      />

      <div className={styles.footerPowered}>
        Desenvolvido por <b>Engehall Mind</b>
      </div>

      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        feedbackText={detailedFeedbackText}
        onFeedbackTextChange={(e) => setDetailedFeedbackText(e.target.value)}
        theme={theme}
      />
    </div>
  );
}